import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';

import { ICccSearchParameters } from '../../../models/CccSearchParameters';
import { CccReportService } from '../../../services/cccReport/ccc-report.service';
import { ExportFileService } from '../../../services/exportFile/export-file.service';
import { UserService } from '../../../services/user/user.service';
import { ExportData } from '../../../Xrefs/xref-display/xref-display.component';
import {
  XrefColumnDisplayOrder,
  XrefColumnRename,
} from '../../../Xrefs/xref-display/xrefDisplayColumns';
import { XRefs } from '../../../Xrefs/xrefsDataModel';
import { ExportFileDialogComponent } from '../../dialogs/export-file-dialog/export-file-dialog.component';
import { FileDownloadComponent } from '../../file-download/file-download.component';
import { ICccReportParameters } from '../../page-components/Parameters/ccc-report-parameters/cccReportParameters';

@Component({
  selector: 'app-ccc-report-page',
  templateUrl: './ccc-report.component.html',
  styleUrls: ['./ccc-report.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class CccReportComponent {
  internalUser: boolean = false;
  searchInProgress: boolean = false;
  hasSearched: boolean = false;
  loadError: boolean = false;

  resultsData: XRefs[] = [];
  selectedTabIndex: number = 0;
  searchParameters: ICccSearchParameters;

  columnOrderSetUp: XrefColumnDisplayOrder[] = [
    { defaultVisible: true, externalAllowed: false, order: 1, key: 'fromPim' },
    {
      defaultVisible: true,
      externalAllowed: true,
      order: 2,
      key: 'fromPartNumber',
    },
    { defaultVisible: true, externalAllowed: true, order: 3, key: 'fromName' },
    { defaultVisible: true, externalAllowed: true, order: 4, key: 'fromDesc' },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 5,
      key: 'fromItemType',
    },
    { defaultVisible: true, externalAllowed: false, order: 6, key: 'toPim' },
    {
      defaultVisible: true,
      externalAllowed: true,
      order: 7,
      key: 'toPartNumber',
    },
    { defaultVisible: true, externalAllowed: true, order: 8, key: 'toName' },
    { defaultVisible: true, externalAllowed: true, order: 9, key: 'toDesc' },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 10,
      key: 'toItemType',
    },
    {
      defaultVisible: false,
      externalAllowed: true,
      order: 11,
      key: 'sourceCount',
    },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 12,
      key: 'sourceType',
    },
    {
      defaultVisible: true,
      externalAllowed: true,
      order: 13,
      key: 'categorizationRank',
    },
    {
      defaultVisible: false,
      externalAllowed: true,
      order: 14,
      key: 'confidenceScore',
    },
    // TODO columns were previous removed and combined
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 15,
      key: 'fromUNSPSCCode',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 16,
      key: 'toUNSPSCCode',
    },
    {
      defaultVisible: false,
      externalAllowed: true,
      order: 17,
      key: 'fromUNSPSCDesc',
    },
    {
      defaultVisible: false,
      externalAllowed: true,
      order: 18,
      key: 'toUNSPSCDesc',
    },
    { defaultVisible: true, externalAllowed: true, order: 19, key: 'fromPsc' },
    { defaultVisible: true, externalAllowed: true, order: 20, key: 'toPsc' },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 21,
      key: 'fromContrNum',
    },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 22,
      key: 'fromNationalContractFlag',
    },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 23,
      key: 'toContrNum',
    },
    {
      defaultVisible: true,
      externalAllowed: false,
      order: 24,
      key: 'toNationalContractFlag',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 25,
      key: 'fromSyncCode',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 26,
      key: 'toSyncCode',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 27,
      key: 'fromSyncGroupDesc',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 28,
      key: 'toSyncGroupDesc',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 29,
      key: 'fromSyncDesc',
    },
    {
      defaultVisible: false,
      externalAllowed: false,
      order: 30,
      key: 'toSyncDesc',
    },
    { defaultVisible: true, externalAllowed: false, order: 31, key: 'isValid' },
  ];
  renameColumns: XrefColumnRename[] = [
    {
      newColumnHeaderName: 'Sources',
      newUserReadableName: 'Sources',
      key: 'sourceType',
    },
  ];

  @ViewChild('drawer') drawer: MatDrawer;
  @ViewChild('fileDownload', { static: true })
  fileDownloadComponent: FileDownloadComponent;

  constructor(
    user: UserService,
    public cccService: CccReportService,
    public exportService: ExportFileService,
    public dialog: MatDialog,
  ) {
    user.isInternal.subscribe(v => {
      this.internalUser = v;
    });
  }

  searchClicked(val: ICccReportParameters) {
    this.drawer.toggle();
    this.selectedTabIndex = 0;
    this.resultsData = [];

    const request: ICccSearchParameters = {
      CccReportType: val.CccReportType,
      IncludeAttribution: val.IncludeAttribution,
      IncludeDerived: val.IncludeDerived,
      InputCompany: val.InputVendors,
      OutputCompany: val.OutputVendors,
      ProductSpendCategory: val.ProductSpendCategory,
      SyncSubcategory: val.SyncSubCategory,
      UnspscCode: val.UnspscCode,
      UnspscDescription: val.UnspscDescription,
      Internal: this.internalUser,
      Export: false,
    };
    this.searchInProgress = true;
    this.hasSearched = true;

    this.searchParameters = request;
    this.cccService.GetCCCReport(request).then(result => {
      result.data.forEach(d => {
        d.validationCheckbox = d.isValid;
      });
      this.resultsData = result.data;
      this.searchInProgress = false;
    });
  }

  cancelSearchClicked(): void {
    this.searchInProgress = false;
  }

  toggleDrawer() {
    this.drawer.toggle();
    document.querySelector('#content').scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  //#region Export
  cccReportInternalExports = [
    { name: 'Internal Only', id: 1 },
    { name: 'Member Safe', id: 2 },
  ];
  cccReportExternalExports = [{ name: 'Export', id: 2 }];

  exportSelection(): ExportData[] {
    if (this.internalUser) return this.cccReportInternalExports;
    return this.cccReportExternalExports;
  }

  isMultiExport(): boolean {
    return this.internalUser;
  }

  exportReport(val: any) {
    let memberSafe = val == 2;

    const dialogRef = this.dialog.open(ExportFileDialogComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == '1') this.selectedTabIndex = 1;
    });
    this.searchParameters.Export = true;
    this.searchParameters.CccReportType = memberSafe
      ? 'membersafe'
      : 'internal';
    this.searchParameters.Internal = !memberSafe;
    //let reportType = "";
    //reportType = this.internalUser ? (memberSafe ? "External Member Safe" : "Internal") : "Member Safe";
    //TODO: audit/GA should be addressed later
    //this.gaService.exportEvent("CccReport", reportType);
    //this.auditService.exportEvent("CccReport", reportType);
    this.exportService.exportCccReportResults(this.searchParameters);

    this.fileDownloadComponent.ExternalRefresh();
  }
  //#endregion
}
